import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

/**
 * Change the vehicle on an existing work order.
 *
 * Opening a work order on the wrong registration is an ordinary intake typo, but
 * it used to be uncorrectable — the serializer silently drops an incoming vehicle,
 * so the workshop had to cancel and start over, burning a work number.
 *
 * The backend refuses the change once the work order has been invoiced (the
 * invoice snapshots the registration). The caller hides the entry point in that
 * case, so this dialog should normally never surface that error.
 */
function ChangeVehicleDialog({ t, open, handleClose, maintenance, setMaintenance }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [moveReadings, setMoveReadings] = useState(true);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setSearchInput('');
      setError(null);
      setMoveReadings(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await axios.get('/api/vehicles/', {
          params: { search: searchInput || undefined, page_size: 20 },
        });
        if (cancelled) return;
        const results = response.data?.results ?? response.data ?? [];
        // Never offer the vehicle it is already on.
        setOptions(results.filter((v) => v.id !== maintenance?.vehicle?.id));
      } catch (err) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchInput, open, maintenance?.vehicle?.id]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.patch(
        `/api/maintenances/${maintenance.id}/set_vehicle/`,
        { vehicle_id: selected.id, move_odometer_readings: moveReadings },
      );
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      // Show the backend's translated reason (e.g. already invoiced) when there is
      // one, never a raw exception.
      setError(err.response?.data?.detail || t('Changing the vehicle failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const currentReg = maintenance?.vehicle?.registration_number || '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Change vehicle')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('Current vehicle')}: <strong>{currentReg}</strong>
        </Typography>

        <Autocomplete
          options={options}
          value={selected}
          onChange={(e, value) => setSelected(value)}
          onInputChange={(e, value) => setSearchInput(value)}
          loading={searchLoading}
          getOptionLabel={(option) =>
            [option.registration_number, option.manufacturer, option.model]
              .filter(Boolean)
              .join(' ')
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t('New vehicle')}
              placeholder={t('Search by registration number')}
              data-testid="change-vehicle-search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={moveReadings}
              onChange={(e) => setMoveReadings(e.target.checked)}
            />
          }
          label={t('Move this work order’s odometer readings to the new vehicle')}
        />

        {selected && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              {t(
                'The work order moves to this vehicle’s service history.',
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selected || submitting}
          data-testid="change-vehicle-submit"
        >
          {submitting ? <CircularProgress size={20} /> : t('Change vehicle')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(ChangeVehicleDialog);
